import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download } from "lucide-react";
import {
  pirsApi,
  downloadBlob,
  type PirGroup,
  type PirDisbursement,
} from "@/api/pirs";
import { Badge, Button } from "@/design-system/components/ui";
import { PageHeader } from "@/components/layout/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useDateFormatter } from "@/utils/datetime";
import { pirKeys } from "./pirKeys";

/**
 * Payment System v3.0 PR 5 — PIR detail (design spec §10.6).
 *
 * Renders the PIR header summary and disbursements grouped by source
 * account. Each row carries its instructed state and (if the operator
 * holds PIR.MarkInstructed) a per-row Mark Instructed button — used
 * when a disbursement needs sealing outside of a PIR generation.
 */
export function PirDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canMark = hasPermission("PIR.MarkInstructed");
  const { dateTime } = useDateFormatter();

  const detail = useQuery({
    queryKey: pirKeys.detail(id),
    queryFn: () => pirsApi.get(id!),
    enabled: !!id,
  });

  if (!id) return null;
  if (detail.isLoading) return <p>Loading…</p>;
  if (detail.isError) return <p>Failed to load PIR.</p>;
  const pir = detail.data;
  if (!pir) return null;

  return (
    <>
      <PageHeader
        eyebrow="PIRs"
        title={pir.reportNumber}
        description={`Generated ${dateTime(pir.generatedAt)} by ${
          pir.generatedByUserId
        } • ${pir.totalDisbursements} disbursements totalling $${pir.totalAmount.toLocaleString(
          undefined,
          { minimumFractionDigits: 2, maximumFractionDigits: 2 },
        )}`}
        actions={
          <div className="flex gap-space-2">
            <Button
              onClick={async () => {
                const blob = await pirsApi.downloadCsv(pir.id);
                downloadBlob(blob, `${pir.reportNumber}.csv`);
              }}
            >
              <Download size={16} aria-hidden /> CSV
            </Button>
            <Button
              onClick={async () => {
                const blob = await pirsApi.downloadPdf(pir.id);
                downloadBlob(blob, `${pir.reportNumber}.pdf`);
              }}
            >
              <Download size={16} aria-hidden /> PDF
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-space-4">
        {pir.groups.map((group) => (
          <SourceAccountSection
            key={group.sourceAccountKey || "unknown"}
            pirId={pir.id}
            group={group}
            canMark={canMark}
          />
        ))}
      </div>
    </>
  );
}

function SourceAccountSection({
  pirId,
  group,
  canMark,
}: {
  pirId: string;
  group: PirGroup;
  canMark: boolean;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const groupTotal = group.disbursements.reduce((sum, d) => sum + d.amount, 0);

  return (
    <section className="rounded-md border border-pearl bg-white shadow-sm">
      <header className="flex items-center justify-between px-space-4 py-space-3 border-b border-pearl">
        <button
          type="button"
          className="text-left font-semibold"
          onClick={() => setCollapsed((c) => !c)}
        >
          {group.sourceAccountDisplay || group.sourceAccountKey || "Unknown source"}{" "}
          <span className="text-graphite-warm font-normal">
            ({group.sourceAccountKey})
          </span>
        </button>
        <span className="text-sm text-graphite-warm">
          {group.disbursements.length} • $
          {groupTotal.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </header>
      {!collapsed && (
        <div className="p-space-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-graphite-warm">
                <th className="py-space-1 pr-space-2">Date</th>
                <th className="py-space-1 pr-space-2">Recipient</th>
                <th className="py-space-1 pr-space-2">Account</th>
                <th className="py-space-1 pr-space-2">Routing</th>
                <th className="py-space-1 pr-space-2 text-right">Amount</th>
                <th className="py-space-1 pr-space-2">Status</th>
                {canMark && <th className="py-space-1">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {group.disbursements.map((d) => (
                <DisbursementRow
                  key={d.disbursementId}
                  pirId={pirId}
                  disbursement={d}
                  canMark={canMark}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function DisbursementRow({
  pirId,
  disbursement,
  canMark,
}: {
  pirId: string;
  disbursement: PirDisbursement;
  canMark: boolean;
}) {
  const qc = useQueryClient();
  const { date } = useDateFormatter();
  const mark = useMutation({
    mutationFn: () => pirsApi.markInstructed(pirId, disbursement.disbursementId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: pirKeys.detail(pirId) });
      // The list rows show instructed state — refresh it too.
      qc.invalidateQueries({ queryKey: pirKeys.list });
    },
  });
  const routing = [
    disbursement.recipientBank,
    disbursement.recipientAba ? `ABA ${disbursement.recipientAba}` : "",
    disbursement.recipientAccountNumber,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <tr className="border-t border-pearl">
      <td className="py-space-1 pr-space-2 align-top">
        {date(disbursement.date)}
      </td>
      <td className="py-space-1 pr-space-2 align-top">{disbursement.recipientEntity || "—"}</td>
      <td className="py-space-1 pr-space-2 align-top">{disbursement.recipientAccount || "—"}</td>
      <td className="py-space-1 pr-space-2 align-top text-graphite-warm">{routing || "—"}</td>
      <td className="py-space-1 pr-space-2 text-right align-top">
        ${disbursement.amount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}
      </td>
      <td className="py-space-1 pr-space-2 align-top">
        {disbursement.isInstructed ? (
          <Badge status="finalized">Instructed</Badge>
        ) : (
          <Badge status="info">Live</Badge>
        )}
      </td>
      {canMark && (
        <td className="py-space-1 align-top">
          {!disbursement.isInstructed && (
            <Button
              size="sm"
              disabled={mark.isPending}
              onClick={() => mark.mutate()}
            >
              Mark Instructed
            </Button>
          )}
        </td>
      )}
    </tr>
  );
}
